import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { ConfirmDialogService, DataCheckerService, Destroyable, Rental, untilDestroy } from '@tokeet/tv3-platform'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import {
  DeleteDiscountCode,
  DiscountCodeGuard,
  DiscountCodeView,
  selectDiscountCodesLoaded,
  selectDiscountCodeViewsByRentalId,
} from '@tokeet-frontend/discount-codes'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { select, Store } from '@ngrx/store'
import { combineLatest } from 'rxjs'
import { FormBuilder } from '@angular/forms'
import { TableService } from '@tv3/services/utils/table.service'
import { createLocaleCompareSort } from '@tokeet-frontend/utility'
import {
  DeleteLosDiscount,
  LosDiscountDialogService,
  LosDiscountGuard,
  LosDiscountView,
  selectLosDiscountViewsByRentalId,
} from '@tokeet-frontend/los-discount'
import * as lodash from 'lodash'
import { DiscountCodeDialogService } from '@tv3/containers/discounts/dialogs/discount-code-dialog.service'
import { environment } from '../../../../environments/environment'

interface RentalDiscountItem {
  name: string
  discountTypeView: string
  discountType: 'code' | 'los'
  type: string
  amount: number
  start: number
  end: number
  startView: string
  endView: string
  isUsed?: boolean
  item: LosDiscountView | DiscountCodeView
}

@Component({
  selector: 'app-rental-discounts',
  templateUrl: './rental-discounts.component.html',
  styleUrls: ['./rental-discounts.component.scss'],
})
export class RentalDiscountsComponent extends Destroyable implements OnInit {
  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort
  @Input() rental: Rental

  displayedColumns = ['name', 'discountTypeView', 'type', 'amount', 'startView', 'endView', 'edit']

  dataSource = new MatTableDataSource<RentalDiscountItem>()

  tableType = TableType.RentalDiscounts
  isEmptyTable$ = this.tableService.isEmptyTable(this.dataSource)

  items: RentalDiscountItem[] = []
  isLoaded$ = this.store.pipe(select(selectDiscountCodesLoaded))

  filters = this.fb.group({
    discountType: [],
    type: [],
  })

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    protected tableService: TableService,
    private confirmDialog: ConfirmDialogService,
    private dataCheckerService: DataCheckerService,
    private discountCodeDialog: DiscountCodeDialogService,
    private losDiscountDialog: LosDiscountDialogService
  ) {
    super()
    this.dataCheckerService.check([DiscountCodeGuard, LosDiscountGuard])
  }

  ngOnInit() {
    this.dataSource.filterPredicate = this.tableService.filterPredicateFactory(this.displayedColumns)
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = createLocaleCompareSort()

    combineLatest([
      this.store.pipe(select(selectDiscountCodeViewsByRentalId, { id: this.rental.id })),
      this.store.pipe(select(selectLosDiscountViewsByRentalId, { id: this.rental.id })),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([discountCodes, losDiscounts]) => {
        this.items = [...this.processDiscountCodes(discountCodes), ...this.processLosDiscounts(losDiscounts)]
        this.dataSource.data = [...this.items]
      })

    this.filters.valueChanges.pipe(untilDestroy(this)).subscribe(({ discountType, type }) => {
      this.dataSource.data = lodash
        .filter(this.items, (item) => (discountType ? item.discountType === discountType : true))
        .filter((item) => (type ? item.type === type : true))
    })
  }

  processDiscountCodes(items: DiscountCodeView[]): RentalDiscountItem[] {
    return lodash.map(items, (item: DiscountCodeView) => {
      return {
        name: item.name,
        discountTypeView: 'Discount Code',
        discountType: 'code',
        type: item.type,
        amount: item.amount,
        start: item.start,
        end: item.expires,
        startView: item.startView,
        endView: item.expiresView,
        isUsed: !!item.last_used,
        item,
      }
    })
  }

  processLosDiscounts(items: LosDiscountView[]): RentalDiscountItem[] {
    return lodash.map(items, (item: LosDiscountView) => {
      return {
        name: item.name,
        discountTypeView: 'LOS Discount',
        discountType: 'los',
        type: item.type,
        amount: item.amount,
        start: item.start,
        end: item.end,
        startView: item.startView,
        endView: item.endView,
        item,
      }
    })
  }

  onSearch(term: string) {
    this.dataSource.filter = (term + '').trim().toLowerCase()
    this.paginator.firstPage()
  }

  onClearFilter() {
    this.filters.reset()
    this.paginator.firstPage()
  }

  onEdit(item: RentalDiscountItem) {
    if (item.discountType === 'code') {
      this.onEditDiscountCode(item.item as DiscountCodeView)
    } else {
      this.onEditLosDiscount(item.item as LosDiscountView)
    }
  }

  onEditDiscountCode(item: DiscountCodeView) {
    this.discountCodeDialog.open({ item })
  }

  onEditLosDiscount(item: LosDiscountView) {
    this.losDiscountDialog.open({
      item,
      primaryBtnClass: 'btn btn-success text-uppercase',
      cancelBtnClass: 'btn btn-cst-danger text-uppercase',
      hasModality: environment.features.losModality,
      articleId: 'k782tt9e6k',
    })
  }

  onAddDiscountCode() {
    this.discountCodeDialog.open({
      item: { rental_id: this.rental.id },
    })
  }

  onAddLosDiscount() {
    this.losDiscountDialog.open({
      item: { rentals: [this.rental.id] },
      primaryBtnClass: 'btn btn-success text-uppercase',
      cancelBtnClass: 'btn btn-cst-danger text-uppercase',
      hasModality: environment.features.losModality,
      articleId: 'k782tt9e6k',
    })
  }

  onDelete(item: RentalDiscountItem) {
    if (item.discountType === 'code') {
      this.onDeleteDiscountCode(item.item as DiscountCodeView)
    } else {
      this.onDeleteLosDiscount(item.item as LosDiscountView)
    }
  }

  onDeleteDiscountCode(item: DiscountCodeView) {
    this.confirmDialog
      .confirm({
        title: 'Delete discount?',
        body: 'Are you sure you want to delete this discount?',
      })
      .subscribe(() => {
        this.store.dispatch(DeleteDiscountCode({ id: item.id }))
      })
  }

  onDeleteLosDiscount(item: LosDiscountView) {
    this.confirmDialog
      .confirm({
        title: 'Delete discount?',
        body: 'Are you sure you want to delete this discount?',
      })
      .subscribe(() => {
        this.store.dispatch(DeleteLosDiscount({ id: item.id }))
      })
  }
}
